import { useMemo, useState } from 'react';
import {
  getBudgets,
  getCategoryTree,
  getSpendingTree,
  setBudget,
  type Budget as BudgetRow,
  type CategorySpend,
  type CategoryTreeNode,
} from '../api.js';
import { Button, EmptyState, ErrorState, Icon, IconButton, Sheet, useToast } from '../ui/index.js';
import { useLoad } from '../lifeos/useLoad.js';
import { errorText } from '../lifeos/toast.js';
import { dollars, leafLabel, money } from './format.js';
import './reports.css';

/**
 * Budget — the Monarch budget page: one month at a time, a total of what was
 * planned against what went out, then each budget as a bar grouped under its
 * root (Essentials, Lifestyle, Business). Below, whatever was spent in a
 * category nobody budgeted, one tap from getting a budget of its own.
 */

/** Roots a budget can live under, in display order. Income and the holding pen never get one. */
const ROOT_ORDER = ['essentials', 'lifestyle', 'business', 'money'];
const NOT_BUDGETABLE = new Set(['income', 'uncategorized']);

/**
 * "$1,234.5" → 123450. Parsed as a string — `Number('1.005') * 100` is
 * 100.4999…, which rounds to the wrong cent. Null when it isn't a plain
 * non-negative amount with at most two decimals.
 */
export function parseDollarsToCents(input: string): number | null {
  const s = input.replace(/[$,\s]/g, '');
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(s);
  if (!m) return null;
  const whole = m[1] ?? '';
  const frac = m[2] ?? '';
  if (!whole && !frac) return null;
  return Number(whole || '0') * 100 + Number(frac.padEnd(2, '0'));
}

/** Cents → the plain string an amount input shows ("1234.50", "80"). */
function centsToInput(cents: number): string {
  const whole = Math.floor(cents / 100);
  const frac = cents % 100;
  return frac ? `${whole}.${String(frac).padStart(2, '0')}` : String(whole);
}

export interface CategoryOption {
  path: string;
  /** "Lifestyle › Entertainment › Video games". */
  label: string;
  depth: number;
}

/** Every budgetable node, depth-first, with its full chain as the label. */
export function flattenTree(tree: CategoryTreeNode[]): CategoryOption[] {
  const out: CategoryOption[] = [];
  const walk = (nodes: CategoryTreeNode[], chain: string[], depth: number): void => {
    for (const node of nodes) {
      if (depth === 1 && NOT_BUDGETABLE.has(node.key)) continue;
      const labels = [...chain, node.label];
      out.push({ path: node.path, label: labels.join(' › '), depth });
      if (node.children) walk(node.children, labels, depth + 1);
    }
  };
  walk(tree, [], 1);
  return out;
}

const isUnder = (path: string, prefix: string): boolean => path === prefix || path.startsWith(`${prefix}.`);

/** 'YYYY-MM' for a local date. */
function ymOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(ym: string, by: number): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return ymOf(new Date(y, m - 1 + by, 1));
}

function monthTitle(ym: string): string {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

function monthRange(ym: string): { from: string; to: string } {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return { from: new Date(y, m - 1, 1).toISOString(), to: new Date(y, m, 1).toISOString() };
}

/** Bar tone: red over, amber past 85%, otherwise the calm default. */
export function budgetTone(spent: number, amount: number): 'over' | 'near' | 'ok' {
  if (amount <= 0) return spent > 0 ? 'over' : 'ok';
  if (spent > amount) return 'over';
  if (spent / amount > 0.85) return 'near';
  return 'ok';
}

/**
 * Spending in categories no budget covers. A budget on the path itself or an
 * ancestor covers the whole row; budgets on descendants cover their own share,
 * so only the rest is left over.
 */
export function unbudgeted(spend: CategorySpend[], budgets: BudgetRow[]): Array<{ path: string; cents: number }> {
  const out: Array<{ path: string; cents: number }> = [];
  for (const row of spend) {
    const path = row.category;
    if (!path || NOT_BUDGETABLE.has(path.split('.')[0]!)) continue;
    if (budgets.some((b) => isUnder(path, b.category))) continue;
    const covered = budgets.filter((b) => isUnder(b.category, path)).reduce((sum, b) => sum + b.spentCents, 0);
    const cents = row.cents - covered;
    if (cents > 0) out.push({ path, cents });
  }
  return out.sort((a, b) => b.cents - a.cents);
}

type Editing = { mode: 'add'; path?: string } | { mode: 'edit'; budget: BudgetRow };

export default function Budget(): JSX.Element {
  const [month, setMonth] = useState(() => ymOf(new Date()));
  const [editing, setEditing] = useState<Editing | null>(null);

  const tree = useLoad(() => getCategoryTree(), []);
  const data = useLoad(async () => {
    const { from, to } = monthRange(month);
    const [b, s] = await Promise.all([getBudgets(month), getSpendingTree(from, to, { depth: 2 })]);
    return { budgets: b.budgets, spend: s.byCategory };
  }, [month]);

  const options = useMemo(() => flattenTree(tree.data ?? []), [tree.data]);
  const labelOf = useMemo(() => {
    const map = new Map(options.map((o) => [o.path, o.label]));
    const rootLabels = new Map((tree.data ?? []).map((n) => [n.path, n.label]));
    return {
      full: (path: string) => map.get(path) ?? leafLabel(path),
      root: (key: string) => rootLabels.get(key) ?? leafLabel(key),
      /** The label without its root ("Entertainment › Video games"), since the group already says it. */
      short: (path: string, fallback?: string) => {
        const full = map.get(path);
        if (!full) return fallback ?? leafLabel(path);
        const rest = full.split(' › ').slice(1).join(' › ');
        return rest || full;
      },
    };
  }, [options, tree.data]);

  const budgets = data.data?.budgets ?? [];
  // Totals count only top-most budgets, so a parent and its child aren't added twice.
  const top = budgets.filter((b) => !budgets.some((o) => o !== b && o.category !== b.category && isUnder(b.category, o.category)));
  const budgeted = top.reduce((s, b) => s + b.amountCents, 0);
  const spent = top.reduce((s, b) => s + b.spentCents, 0);
  const left = budgeted - spent;

  const groups = useMemo(() => {
    const byRoot = new Map<string, BudgetRow[]>();
    for (const b of budgets) {
      const root = b.category.split('.')[0]!;
      byRoot.set(root, [...(byRoot.get(root) ?? []), b]);
    }
    const order = (r: string) => (ROOT_ORDER.includes(r) ? ROOT_ORDER.indexOf(r) : ROOT_ORDER.length);
    return [...byRoot.entries()]
      .sort((a, b) => order(a[0]) - order(b[0]) || a[0].localeCompare(b[0]))
      .map(([root, rows]) => ({ root, rows: rows.sort((a, b) => a.category.localeCompare(b.category)) }));
  }, [budgets]);

  const loose = data.data ? unbudgeted(data.data.spend, budgets) : [];

  return (
    <div className="mr-page">
      <div className="mr-month" role="group" aria-label="Month">
        <IconButton icon="chevron-left" label="Previous month" onClick={() => setMonth((m) => shiftMonth(m, -1))} />
        <span className="mr-month__label" aria-live="polite">
          {monthTitle(month)}
        </span>
        <IconButton icon="chevron-right" label="Next month" onClick={() => setMonth((m) => shiftMonth(m, 1))} />
        <span className="mr-month__spacer" />
        <Button size="sm" icon="plus" onClick={() => setEditing({ mode: 'add' })}>
          Add budget
        </Button>
      </div>

      {data.error && !data.data ? (
        <ErrorState message={data.error} onRetry={() => void data.reload()} />
      ) : !data.data ? (
        <div className="mr-loading" aria-busy="true">
          Loading…
        </div>
      ) : (
        <>
          <section className="mr-card mr-summary" aria-label="Budget summary">
            <div className="mr-summary__fig">
              <span className="mr-summary__label">Budgeted</span>
              <span className="mr-summary__value">{dollars(budgeted)}</span>
            </div>
            <div className="mr-summary__fig">
              <span className="mr-summary__label">Spent</span>
              <span className="mr-summary__value">{dollars(spent)}</span>
            </div>
            <div className="mr-summary__fig">
              <span className="mr-summary__label">{left < 0 ? 'Over' : 'Left'}</span>
              <span className={`mr-summary__value${left < 0 ? ' mr-bad' : ' mr-good'}`}>{dollars(Math.abs(left))}</span>
            </div>
            {budgeted > 0 && <Progress spent={spent} amount={budgeted} label="Total spent of budgeted" />}
          </section>

          {groups.length === 0 ? (
            <EmptyState
              icon="target"
              title="No budgets yet"
              hint="Set a monthly amount for any category — a whole root like Lifestyle, or one leaf like Video games."
              action={
                <Button icon="plus" onClick={() => setEditing({ mode: 'add' })}>
                  Add budget
                </Button>
              }
            />
          ) : (
            groups.map((g) => {
              const gAmount = g.rows.reduce((s, b) => s + b.amountCents, 0);
              const gSpent = g.rows.reduce((s, b) => s + b.spentCents, 0);
              return (
                <section key={g.root} className="mr-card mr-group" aria-label={labelOf.root(g.root)}>
                  <header className="mr-group__head">
                    <h3 className="mr-group__title">{labelOf.root(g.root)}</h3>
                    <span className="mr-group__sum">
                      {dollars(gSpent)} <span className="mr-muted">of {dollars(gAmount)}</span>
                    </span>
                  </header>
                  <ul className="mr-list">
                    {g.rows.map((b) => {
                      const tone = budgetTone(b.spentCents, b.amountCents);
                      const rem = b.amountCents - b.spentCents;
                      return (
                        <li key={b.category}>
                          <button
                            type="button"
                            className="mr-budget"
                            aria-label={`Edit budget ${labelOf.full(b.category)}`}
                            onClick={() => setEditing({ mode: 'edit', budget: b })}
                          >
                            <span className="mr-budget__head">
                              <span className="mr-budget__name">{labelOf.short(b.category, b.label)}</span>
                              <span className="mr-budget__amt">
                                <b>{dollars(b.spentCents)}</b> <span className="mr-muted">/ {dollars(b.amountCents)}</span>
                              </span>
                            </span>
                            <Progress spent={b.spentCents} amount={b.amountCents} />
                            <span className={`mr-budget__left${tone === 'over' ? ' mr-bad' : ''}`}>
                              {rem >= 0 ? `${dollars(rem)} left` : `${dollars(-rem)} over`}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })
          )}

          {loose.length > 0 && (
            <section className="mr-card mr-group" aria-label="Unbudgeted spending">
              <header className="mr-group__head">
                <h3 className="mr-group__title">Unbudgeted spending</h3>
                <span className="mr-group__sum">{dollars(loose.reduce((s, r) => s + r.cents, 0))}</span>
              </header>
              <ul className="mr-list">
                {loose.map((r) => (
                  <li key={r.path} className="mr-loose">
                    <span className="mr-loose__name">{labelOf.full(r.path)}</span>
                    <span className="mr-loose__amt">{money(r.cents)}</span>
                    <button
                      type="button"
                      className="mr-chipbtn"
                      aria-label={`Budget ${labelOf.full(r.path)}`}
                      onClick={() => setEditing({ mode: 'add', path: r.path })}
                    >
                      <Icon name="plus" size={14} /> budget
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {editing && (
        <BudgetSheet
          editing={editing}
          options={options}
          existing={budgets}
          labelOf={labelOf.full}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void data.reload();
          }}
        />
      )}
    </div>
  );
}

function Progress({ spent, amount, label }: { spent: number; amount: number; label?: string }): JSX.Element {
  const tone = budgetTone(spent, amount);
  const pct = amount > 0 ? Math.min(100, Math.round((spent / amount) * 100)) : spent > 0 ? 100 : 0;
  return (
    <span
      className={`mr-progress mr-progress--${tone}`}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
    >
      <span className="mr-progress__fill" style={{ width: `${pct}%` }} />
    </span>
  );
}

function BudgetSheet({
  editing,
  options,
  existing,
  labelOf,
  onClose,
  onSaved,
}: {
  editing: Editing;
  options: CategoryOption[];
  existing: BudgetRow[];
  labelOf: (path: string) => string;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const initialPath = editing.mode === 'edit' ? editing.budget.category : (editing.path ?? null);
  const [path, setPath] = useState<string | null>(initialPath);
  const [query, setQuery] = useState('');
  const [amount, setAmount] = useState(editing.mode === 'edit' ? centsToInput(editing.budget.amountCents) : '');
  const [busy, setBusy] = useState(false);

  const cents = parseDollarsToCents(amount);
  const valid = path !== null && cents !== null && cents > 0;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    return list.slice(0, 80);
  }, [options, query]);

  const save = async (amountCents: number): Promise<void> => {
    if (path === null) return;
    setBusy(true);
    try {
      await setBudget(path, amountCents);
      onSaved();
    } catch (err) {
      toast.error(errorText(err));
      setBusy(false);
    }
  };

  const title = editing.mode === 'edit' ? 'Edit budget' : 'Add budget';
  const taken = new Set(existing.map((b) => b.category));

  return (
    <Sheet title={title} onClose={onClose} action={{ label: 'Save', onClick: () => cents !== null && void save(cents), disabled: !valid || busy }}>
      <form
        className="hb-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && cents !== null) void save(cents);
        }}
      >
        {editing.mode === 'edit' || initialPath ? (
          <div className="hb-field">
            <span>Category</span>
            <div className="mr-picked">{path ? labelOf(path) : '—'}</div>
          </div>
        ) : (
          <div className="hb-field">
            <span>Category</span>
            <input
              type="search"
              placeholder="Search categories"
              aria-label="Search categories"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <ul className="mr-options" role="listbox" aria-label="Category">
              {shown.map((o) => (
                <li key={o.path}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={path === o.path}
                    className={`mr-option mr-option--d${o.depth}${path === o.path ? ' mr-option--on' : ''}`}
                    onClick={() => setPath(o.path)}
                  >
                    <span className="mr-option__label">{o.label}</span>
                    {taken.has(o.path) && <span className="mr-muted">budgeted</span>}
                    {path === o.path && <Icon name="check" size={18} className="ui-pick__check" />}
                  </button>
                </li>
              ))}
              {shown.length === 0 && <li className="mr-muted mr-options__none">No category matches.</li>}
            </ul>
          </div>
        )}
        <label className="hb-field">
          <span>Monthly amount</span>
          <input
            inputMode="decimal"
            placeholder="$0.00"
            aria-label="Monthly amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          {amount && cents === null && <span className="hb-form__error">Enter an amount like 250 or 249.99.</span>}
        </label>
        <Button type="submit" size="lg" block disabled={!valid || busy}>
          {busy ? 'Saving…' : 'Save budget'}
        </Button>
        {editing.mode === 'edit' && (
          <Button variant="destructive" block disabled={busy} onClick={() => void save(0)}>
            Delete budget
          </Button>
        )}
      </form>
    </Sheet>
  );
}
